/** @jsx isolateComponent */
import { useState } from 'react';
import { css } from '@emotion/core';
import { Colors, useTheme } from '@zapier/design-system';
import { isolateComponent } from '@zapier/style-encapsulation';

import { AlphabeticAppNav } from '../AlphabeticAppNav';
import { Branding } from '../Branding';
import { HelpfulLinks, HelpfulLinksCssVars } from '../HelpfulLinks';
import { SocialIcons } from '../SocialIcons';
import { ZapierMeta, ZapierMetaCssVars } from '../ZapierMeta';
import {
  LinksSection,
  TopSearchesSection,
  PopularAppsSection,
  TrendingAppsSection,
  TopAppsByCategorySection,
  OurBestContentSection,
} from '../LinksSection';
import type { LinkSectionType } from '../LinksSection';

type Props = {
  /**
   * Indicates whether the alphabetic app navigation should render.
   */
  shouldRenderAlphabetNav?: boolean;
  /**
   * Indicates whether the SEO links sections should render.
   */
  shouldRenderSeoLinks?: boolean;
  /**
   * Indicates whether simplified footer variant should render.
   */
  showSimplifiedFooter?: boolean;
};

const mediaQueryMedium = '(min-width: 650px)';
const mediaQueryLarge = '(min-width: 980px)';
const mediaQueryTablet = '(max-width: 980px)';

const Styles = {
  root: (isRebrand: boolean) => css`
    background-color: ${isRebrand ? Colors.neutral100 : '#fff'};
    padding: 30px;
    ${isRebrand &&
    `
      @media ${mediaQueryTablet} {
        padding: 20px;
      }
    `}
  `,
  simplifiedNav: css`
    display: flex;
    justify-content: center;
    justify-items: center;
  `,
  // prettier-ignore
  grid: (props: Props) => css`
    display: grid;
    grid-template-rows: auto;
    grid-template-columns: 100%;
    grid-template-areas:
      ${props.shouldRenderAlphabetNav ? '"alphabetNav"' : ''}
      ${props.shouldRenderSeoLinks ? '"seoLinks"' : ''}
      "helpfulLinks"
      "socialIcons"
      "branding"
      "zapierMeta";
    justify-items: center;
    align-items: center;
    gap: 30px;
    max-width: 1200px;
    margin: 0 auto;

    @media ${mediaQueryMedium} {
      grid-template-columns: max-content 1fr;
      grid-template-areas:
        ${props.shouldRenderAlphabetNav ? '"alphabetNav alphabetNav"' : ''}
        ${props.shouldRenderSeoLinks ? '"seoLinks seoLinks"' : ''}
        "helpfulLinks helpfulLinks"
        "socialIcons socialIcons"
        "branding zapierMeta";
    }

    @media ${mediaQueryLarge} {
      grid-template-columns: max-content 1fr;
      grid-template-areas:
        ${props.shouldRenderAlphabetNav ? '"alphabetNav alphabetNav"' : ''}
        ${props.shouldRenderSeoLinks ? '"seoLinks seoLinks"' : ''}
        "socialIcons helpfulLinks"
        "branding zapierMeta";
    }
  `,
  branding: css`
    grid-area: branding;
    @media ${mediaQueryMedium} {
      justify-self: start;
    }
  `,
  helpfulLinks: css`
    grid-area: helpfulLinks;
    width: 100%;
    display: flex;
    justify-content: center;
    @media ${mediaQueryMedium} {
      ${HelpfulLinksCssVars.flexDirection}: row;
    }
    @media ${mediaQueryLarge} {
      justify-content: flex-end;
      ${HelpfulLinksCssVars.justifyContent}: flex-end;
    }
  `,
  alphabetNav: (isRebrand: boolean) => css`
    grid-area: alphabetNav;

    ${!isRebrand &&
    `// Gives nice wrapping and prevents widows on smaller screens.
    max-width: 30ch;`}

    @media ${mediaQueryMedium} {
      max-width: none;
    }
  `,
  seoLinks: css`
    grid-area: seoLinks;
    width: 100%;
  `,
  socialIcons: css`
    grid-area: socialIcons;
    @media ${mediaQueryLarge} {
      justify-self: start;
    }
  `,
  zapierMeta: css`
    grid-area: zapierMeta;
    @media ${mediaQueryMedium} {
      justify-self: end;
      ${ZapierMetaCssVars.justifyContent}: flex-end;
    }
  `,
  linksSections: css`
    display: grid;
    grid-template-columns: 100%;
    text-align: center;
    column-gap: 10px;
    row-gap: 20px;
    @media (min-width: 420px) {
      text-align: left;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }
  `,
};

const LinksSections = (props: { sections: Array<LinkSectionType> }) => {
  const [areSectionsExpanded, setAreSectionsExpanded] = useState(false);
  const toggleAreSectionsExpanded = () => {
    setAreSectionsExpanded(!areSectionsExpanded);
  };
  return (
    <div css={Styles.linksSections}>
      {props.sections.map((section: LinkSectionType) => (
        <LinksSection
          // Had funky race issues with `details[open`],
          // so use `key` to force a remount. Should be
          // totally fine for this context.
          key={section.title + areSectionsExpanded}
          title={section.title}
          links={section.links}
          isExpanded={areSectionsExpanded}
          onToggleExpanded={toggleAreSectionsExpanded}
        />
      ))}
    </div>
  );
};

/**
 * The footer that renders at the bottom of most pages.
 */
export const UniversalFooter = (props: Props) => {
  const { isRebrand } = useTheme();

  const alphabetNav = props.shouldRenderAlphabetNav && (
    <div css={Styles.alphabetNav(isRebrand)}>
      <AlphabeticAppNav />
    </div>
  );
  const seoLinks = props.shouldRenderSeoLinks && (
    <div css={Styles.seoLinks}>
      <LinksSections
        sections={[
          TopSearchesSection,
          PopularAppsSection,
          TrendingAppsSection,
          TopAppsByCategorySection,
          OurBestContentSection,
        ]}
      />
    </div>
  );

  return (
    <footer
      css={Styles.root(isRebrand)}
      role="contentinfo"
      data-testid="universalFooter"
    >
      {props.showSimplifiedFooter ? (
        <div css={Styles.simplifiedNav}>
          <ZapierMeta
            links={[
              { text: 'Legal', href: '/legal' },
              { text: 'Privacy', href: '/privacy' },
              // This `className` is defined by OneTrust and works as a hook to
              // open the cookie preferences modal. The `style` hides the link until
              // OneTrust initializes so that the button only renders once it'll do something.
              {
                text: 'Manage cookies',
                className: 'ot-sdk-show-settings',
                style: { display: 'none' },
              },
            ]}
          />
        </div>
      ) : (
        <div css={Styles.grid(props)}>
          <div css={Styles.helpfulLinks}>
            <HelpfulLinks />
          </div>
          {alphabetNav}
          {seoLinks}
          <div css={Styles.socialIcons}>
            <SocialIcons />
          </div>
          <div css={Styles.branding}>
            <Branding />
          </div>
          <div css={Styles.zapierMeta}>
            <ZapierMeta />
          </div>
        </div>
      )}
    </footer>
  );
};
